<template>
	<ext-dialog
		v-model="visible"
		:title="dialogTitle"
		dialogType="large"
		:primaryButtonText="$t('save')"
		@primaryButtonAction="onSave"
		@closed="onClose"
	>
		<template #body>
			<v-form v-if="variant" ref="form" v-model="valid" lazy-validation>
				<v-row>
					<v-col cols="8">
						<localized-text-field
							v-model="variant.name"
							:defaultLanguage="companyDefaultLanguage"
							:options="companyLanguageOptions"
							:rules="rules.name"
							context="variant"
							:label="$t('name')"
						/>
					</v-col>
					<v-col cols="4">
						<v-text-field v-model="variant.code" :label="$t('code')" :rules="rules.code" dense />
					</v-col>
				</v-row>

				<v-row dense class="mb-4" v-if="attributeOptions.some((a) => a.type === 'multi_select')">
					<v-col
						v-for="attr in attributeOptions.filter((a) => a.type === 'multi_select')"
						:key="attr._id"
						cols="12"
						sm="6"
					>
						<v-select
							v-model="attributeValues[attr._id]"
							:items="attr.options.map((o) => ({ text: o.value[$lang] || o.value._default, value: o._id }))"
							:label="attr.name[$lang] || attr.name._default"
							multiple
							chips
							small-chips
							deletable-chips
							hide-details
							outlined
							append-icon="icon-angle-down"
							:menu-props="{ offsetY: true }"
						/>
					</v-col>
				</v-row>

				<v-row dense class="mb-4">
					<v-col v-for="attr in attributeOptions.filter((a) => a.type !== 'multi_select')" :key="attr._id" cols="auto">
						<v-switch
							v-model="attributeValues[attr._id]"
							:label="attr.name[$lang] || attr.name._default"
							hide-details
						/>
					</v-col>
					<v-col cols="12" sm="4">
						<v-switch v-model="variant.hasGross" :label="$t('separate_gross')" hide-details />
					</v-col>
				</v-row>

				<v-divider class="my-4" />

				<div v-for="(blk, idx) in blocks" :key="idx">
					<v-row dense class="mb-2 align-center">
						<v-col cols="10">
							<v-select
								v-model="blk.label"
								:items="allEntryFormats"
								item-text="label"
								item-value="label"
								:label="$t('format')"
								outlined
								dense
								append-icon="icon-angle-down"
								@change="formatChanged(idx)"
								hide-details
								:menu-props="{ offsetY: true }"
							/>
						</v-col>
						<v-col cols="2" class="d-flex justify-end">
							<v-btn icon small color="red" @click="removeBlock(idx)" v-if="blocks.length > 1">
								<v-icon>icon-delete</v-icon>
							</v-btn>
							<v-btn icon small color="primary" @click="addBlock" v-if="idx === 0">
								<v-icon>icon-plus</v-icon>
							</v-btn>
						</v-col>
					</v-row>

					<v-row dense v-if="isScalar(blk)">
						<v-col cols="6">
							<v-text-field
								v-model.number="blk.scalar"
								:label="variant.hasGross ? $t('fixed_value') + ` (${$t('net')})` : $t('fixed_value')"
								dense
								hide-details
							/>
						</v-col>
						<v-col cols="6" v-if="variant.hasGross">
							<v-text-field
								v-model.number="blk.grossScalar"
								:label="$t('fixed_value') + ` (${$t('gross')})`"
								dense
								hide-details
							/>
						</v-col>
					</v-row>

					<v-row dense v-else>
						<v-col v-for="f in blk.fields" :key="blk.label + '-' + f" class="d-flex flex-column">
							<v-text-field
								v-model.number="blk.measurements[f]"
								:label="variant.hasGross ? $t(f) + ` (${$t('net')})` : $t(f)"
								dense
								hide-details
							/>
							<v-text-field
								v-if="variant.hasGross"
								class="mt-2"
								v-model.number="blk.gross[f]"
								:label="$t(f) + ` (${$t('gross')})`"
								dense
								hide-details
							/>
						</v-col>
						<v-col cols="12" sm="2">
							<v-select
								v-model="blk.unit"
								:items="unitChoices(blk)"
								item-text="code"
								item-value="code"
								dense
								hide-details
								append-icon="icon-angle-down"
								:menu-props="{ offsetY: true }"
							/>
						</v-col>
					</v-row>

					<v-divider v-if="idx < blocks.length - 1" class="my-4" />
				</div>
			</v-form>
		</template>
	</ext-dialog>
</template>

<script>
import ExtDialog from "../../../partials/dialogs/ExtDialog.vue";
import LocalizedTextField from "../../../controls/LocalizedTextField.vue";
import uiMixin from "../../../../store/interfaces/ui.mixin";
import { mapGetters } from "vuex";

export default {
	name: "VariantDialog",
	components: { ExtDialog, LocalizedTextField },
	mixins: [uiMixin],
	props: {
		value: Boolean,
		variantData: Object,
		unitOptions: Array,
		allUnits: Array,
		productName: String,
		productCode: String,
		attributeOptions: Array,
	},
	data() {
		return {
			visible: false,
			valid: false,
			variant: null,
			blocks: [],
			attributeValues: {},
			rules: {
				name: [
					(v) => !!v || this.$t("$validation.required", { item: this.$t("name") }),
					(v) =>
						Object.values(v).every((s) => s.length <= 100) ||
						this.$t("$validation.max_length", { item: this.$t("name"), length: "100" }),
				],
				code: [
					(v) => !!v || this.$t("$validation.required", { item: this.$t("code") }),
					(v) => v.length <= 20 || this.$t("$validation.max_length", { item: this.$t("code"), length: "20" }),
				],
			},
		};
	},
	computed: {
		...mapGetters(["companyDefaultLanguage", "companyLanguageOptions"]),
		dialogTitle() {
			return this.variantData ? this.$t("edit_variant") : this.$t("add_variant");
		},
		allEntryFormats() {
			return [...new Map(this.unitOptions.flatMap((u) => u.entryFormats || []).map((f) => [f.label, f])).values()].sort(
				(a, b) => a.label.localeCompare(b.label)
			);
		},
		$lang() {
			return this.$i18n.locale;
		},
	},
	watch: {
		value(v) {
			this.visible = v;
			if (v) this.initVariant();
		},
		visible(v) {
			this.$emit("input", v);
		},
	},
	methods: {
		isScalar(blk) {
			return blk.fields.length === 0;
		},
		formatObj(lbl, id) {
			const byId = this.allEntryFormats.find((f) => f._id.toString() === id.toString());
			if (byId) return byId;
			return this.allEntryFormats.find((f) => f.label === lbl) || { requiredMeasurements: [] };
		},
		unitChoices(blk) {
			const dims = blk.fields.length > 0;
			const base = this.unitOptions.find((u) => (u.entryFormats || []).some((f) => f.label === blk.label));
			const needed = dims ? "length" : base?.measurementType;
			return this.allUnits.filter((u) => u.measurementType === needed);
		},
		makeBlock(label, existing) {
			const fmt = existing?.formatId ? this.formatObj(label, existing.formatId) : this.formatObj(label, "");
			const lbl = fmt.label || this.allEntryFormats[0]?.label || "";
			const blk = {
				formatId: fmt._id || null,
				measurementId: existing?._id || null,
				label: lbl,
				fields: fmt.requiredMeasurements || [],
				measurements: {},
				gross: {},
				scalar: null,
				grossScalar: null,
				unit: "",
			};
			if (!blk.fields.length) {
				blk.scalar = existing?.value ?? null;
				blk.grossScalar = existing?.grossValue ?? null;
			} else {
				blk.fields.forEach((f) => {
					blk.measurements[f] = existing?.[f] ?? null;
					blk.gross[f] = existing?.[`gross${f.charAt(0).toUpperCase() + f.slice(1)}`] ?? null;
				});
				blk.unit = existing?.unit || this.unitChoices(blk)[0]?.code || "";
			}
			return blk;
		},
		formatChanged(i) {
			this.$set(this.blocks, i, this.makeBlock(this.blocks[i].label));
		},
		addBlock() {
			this.blocks.push(this.makeBlock());
		},
		removeBlock(i) {
			this.blocks.splice(i, 1);
		},
		initVariant() {
			this.attributeValues = {};
			if (this.variantData) {
				this.variant = {
					_id: this.variantData._id,
					code: this.variantData.code,
					name: { ...this.variantData.name },
					hasGross:
						!!this.variantData.hasGross ||
						(this.variantData.measurements || []).some((m) =>
							Object.keys(m).some((k) => k.toLowerCase().includes("gross") && m[k] != null)
						),
				};
				(this.variantData.attributes || []).forEach((a) => {
					const def = this.attributeOptions.find((x) => x._id.toString() === a.attributeId);
					if (!def) return;
					if (def.type === "multi_select") {
						const arr = Array.isArray(a.value) ? a.value : a.value ? [a.value] : [];
						this.attributeValues[a.attributeId] = [...arr];
					} else {
						this.attributeValues[a.attributeId] = true;
					}
				});
			} else {
				this.variant = {
					code: `${this.productCode}-`,
					name: { [this.companyDefaultLanguage]: `${this.productName} - ` },
					hasGross: false,
				};
			}
			this.blocks = this.variantData?.measurements?.length
				? this.variantData.measurements.map((m) => this.makeBlock(m.format, m))
				: [this.makeBlock()];
			this.companyLanguageOptions.forEach((l) => {
				if (!this.variant.name[l]) this.variant.name[l] = "";
			});
		},
		onSave() {
			this.$refs.form.validate();
			if (!this.valid) return;
			const measurementsArr = this.blocks.map((blk) => {
				const obj = {
					...(blk.measurementId && { _id: blk.measurementId }),
					formatId: blk.formatId,
					format: blk.label,
					unit: blk.unit,
				};
				if (this.isScalar(blk)) {
					obj.value = blk.scalar;
					if (this.variant.hasGross) obj.grossValue = blk.grossScalar;
				} else {
					Object.assign(obj, blk.measurements);
					if (this.variant.hasGross) {
						Object.entries(blk.gross).forEach(([k, v]) => {
							obj[`gross${k.charAt(0).toUpperCase() + k.slice(1)}`] = v;
						});
					}
				}
				return obj;
			});
			const attrsArr = [];
			this.attributeOptions.forEach((attr) => {
				const val = this.attributeValues[attr._id];
				if (attr.type === "multi_select") {
					if (Array.isArray(val) && val.length) attrsArr.push({ attributeId: attr._id, value: val });
				} else if (val) attrsArr.push({ attributeId: attr._id });
			});
			this.$emit("save", { ...this.variant, measurements: measurementsArr, attributes: attrsArr });
			this.visible = false;
		},
		onClose() {
			this.visible = false;
		},
	},
};
</script>
